import type { UploadedFileMetadata } from '@/api/workflowRunFiles'
import { BaseBranchHint } from '@/components/projects/workflows/shared/BaseBranchHint'
import { BranchNameHint } from '@/components/projects/workflows/shared/BranchNameHint'
import { RepoBranchMapField } from '@/components/projects/workflows/shared/RepoBranchMapField'
import { RepoFilePickerField } from '@/components/projects/workflows/shared/RepoFilePickerField'
import { dedupeRepositoryContextSources } from '@/components/projects/workflows/shared/contextSourceDedup'
import { validateRepoFilePickerValue } from '@/components/projects/workflows/shared/repoFilePickerUtils'
import { buildTextFieldRules } from '@/components/projects/workflows/shared/triggerFieldRules'

import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/multi-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useConnectors, useContextSources, useTestPlans } from '@/hooks'
import { databaseConnectorLabel, parseDatabaseMetadata } from '@/components/projects/connectors/databaseConnectorMetadata'
import { isFieldVisible } from '@/components/projects/workflows/chat/TriggerField'
import { useDeleteWorkflowRunFile, useUploadWorkflowRunFiles } from '@/hooks/useWorkflowRunFiles'

import { extractValidationMessage } from '@/lib/validationErrors'
import { cn } from '@/lib/utils'
import { describedBy, FIELD_HINT_CLASS, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS, formatLabel } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { TriggerConnectorRef, TriggerInputField } from '@/types'
import { File as FileIcon, FileText, Image, Loader2, Trash2, Upload } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'



const CONNECTOR_STATUS_VARIANTS: Record<string, { classes: string; label: string }> = {
  connected: { classes: 'border-success-foreground bg-success text-success-foreground', label: 'Connected' },
  expired: { classes: 'border-warning-foreground bg-warning text-warning-foreground', label: 'Expired' },
  not_configured: { classes: 'border-danger-foreground bg-danger text-danger-foreground', label: 'Not Configured' },
}


export function ConnectorBadge({ connector }: { connector: TriggerConnectorRef }) {
  const variant = CONNECTOR_STATUS_VARIANTS[connector.status] ?? CONNECTOR_STATUS_VARIANTS.not_configured
  return (
    <span
      className={cn('inline-flex items-center h-6 rounded-full border px-2.5 text-xs font-medium', variant.classes)}
      data-testid={`trigger-connector-${connector.alias}`}
    >
      {connector.connectorType} {variant.label}
    </span>
  )
}

interface PickerItem {
  id: string
  label: string
}

function PickerField({
  field, items, isLoading, loadingText, placeholder, emptyText, testIdSuffix,
  watch, setValue, register, error,
}: {
  field: TriggerInputField
  items: PickerItem[]
  isLoading: boolean
  loadingText: string
  placeholder: string
  emptyText: string
  testIdSuffix: string
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  register: ReturnType<typeof useForm<FormValues>>['register']
  error: { message?: string } | undefined
}) {
  const selectedValue = watch(field.name) ?? ''

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`trigger-${field.name}`} className={FIELD_LABEL_CLASS}>
        {field.label ?? formatLabel(field.name)}
        {field.required && <span className="text-red-500"> *</span>}
      </Label>
      {isLoading ? (
        <div className="flex items-center gap-2 h-10" role="status" aria-live="polite">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{loadingText}</span>
        </div>
      ) : (
        <Select
          value={selectedValue}
          onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}
        >
          <SelectTrigger id={`trigger-${field.name}`} className={FIELD_INPUT_CLASS} data-testid={`trigger-${field.name}-${testIdSuffix}`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
            {items.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyText}</div>
            )}
          </SelectContent>
        </Select>
      )}
      <input type="hidden" {...register(field.name, field.required ? { required: `${field.label ?? formatLabel(field.name)} is required` } : undefined)} />
      {error ? (
        <p className="text-xs text-destructive" role="alert">{error.message as string}</p>
      ) : field.description ? (
        <p className={FIELD_HINT_CLASS}>{field.description}</p>
      ) : null}
    </div>
  )
}

function ContextPickerField({
  field, projectId, watch, setValue, register, error,
}: {
  field: TriggerInputField
  projectId: string
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  register: ReturnType<typeof useForm<FormValues>>['register']
  error: { message?: string } | undefined
}) {
  const { data: contextData, isLoading } = useContextSources(projectId, { maxResultCount: 1000 })

  const items: PickerItem[] = useMemo(() => {
    const rawItems = contextData?.items ?? []
    const filtered = field.contextType
      ? rawItems.filter((s: { kind?: string }) => s.kind?.toLowerCase() === field.contextType?.toLowerCase())
      : rawItems
    return dedupeRepositoryContextSources(filtered)
      .map((s: { id: string; name: string; kind?: string }) => ({
        id: s.id,
        label: s.kind ? `${s.name} (${s.kind})` : s.name,
      }))
  }, [contextData?.items, field.contextType])

  if (field.multiSelect) {
    const multiSelectOptions = items.map((item) => ({ value: item.id, label: item.label }))
    const rawValue = watch(field.name) ?? ''
    const selectedValues: string[] = (() => {
      if (Array.isArray(rawValue)) return rawValue
      if (typeof rawValue === 'string' && rawValue.startsWith('[')) {
        try { return JSON.parse(rawValue) } catch { return [] }
      }
      return rawValue ? [rawValue] : []
    })()

    return (
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {field.label ?? formatLabel(field.name)}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-2">Loading context sources...</div>
        ) : (
          <MultiSelect
            options={multiSelectOptions}
            value={selectedValues}
            onChange={(newValue) => setValue(field.name, JSON.stringify(newValue), { shouldValidate: true })}
            placeholder="Select context sources..."
            displayMode="badges"
          />
        )}
        <input type="hidden" {...register(field.name, field.required ? { required: `${field.label ?? formatLabel(field.name)} is required` } : undefined)} />
        {error ? (
          <p className="text-xs text-destructive" role="alert">{error.message as string}</p>
        ) : field.description ? (
          <p className={FIELD_HINT_CLASS}>{field.description}</p>
        ) : null}
      </div>
    )
  }

  return (
    <PickerField
      field={field} items={items} isLoading={isLoading}
      loadingText="Loading context sources..." placeholder="Select context source..."
      emptyText="No context sources available" testIdSuffix="context-picker"
      watch={watch} setValue={setValue} register={register} error={error}
    />
  )
}

function ConnectorPickerField({
  field, projectId, watch, setValue, register, error,
}: {
  field: TriggerInputField
  projectId: string
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  register: ReturnType<typeof useForm<FormValues>>['register']
  error: { message?: string } | undefined
}) {
  const { data: connectors, isLoading } = useConnectors(projectId)

  const items: PickerItem[] = useMemo(() => {
    if (!Array.isArray(connectors)) return []
    const filtered = field.connectorType
      ? connectors.filter((c) => c.type.toLowerCase() === field.connectorType?.toLowerCase())
      : connectors.filter((c) => c.category !== 'Database' && c.category !== 'CodeQuality' && c.category !== 'AiProvider')
    return filtered.map((c) => ({
      id: c.id,
      label: c.category === 'Database' ? databaseConnectorLabel(c) : c.type,
    }))
  }, [connectors, field.connectorType])

  const selectedId = watch(field.name)
  useEffect(() => {
    if (field.connectorType?.toLowerCase() !== 'relationaldatabase') return
    const c = Array.isArray(connectors) ? connectors.find((x) => x.id === selectedId) : undefined
    setValue(`${field.name}__dbType`, c ? (parseDatabaseMetadata(c.providerMetadata)?.dbType ?? '') : '')
  }, [selectedId, connectors, field.name, field.connectorType, setValue])

  return (
    <PickerField
      field={field} items={items} isLoading={isLoading}
      loadingText="Loading connectors..." placeholder="Select connector..."
      emptyText="No connectors available" testIdSuffix="connector-picker"
      watch={watch} setValue={setValue} register={register} error={error}
    />
  )
}

function TestSuitePickerField({
  field, projectId, watch, setValue, register, error,
}: {
  field: TriggerInputField
  projectId: string
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  register: ReturnType<typeof useForm<FormValues>>['register']
  error: { message?: string } | undefined
}) {
  const { data: plansData, isLoading } = useTestPlans(projectId, { maxResultCount: 1000, skipCount: 0 })

  const items: PickerItem[] = useMemo(() => {
    const plans = plansData?.items ?? []
    return plans.flatMap(plan =>
      (plan.testSuites ?? []).map(suite => ({
        id: suite.id,
        label: `${plan.title} / ${suite.title}`,
      })),
    )
  }, [plansData])

  return (
    <PickerField
      field={{ ...field, label: field.label ?? 'Test Suite' }} items={items} isLoading={isLoading}
      loadingText="Loading test suites..." placeholder="Select test suite..."
      emptyText="No test suites — create a test plan and suite first"
      testIdSuffix="testsuite-picker"
      watch={watch} setValue={setValue} register={register} error={error}
    />
  )
}

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024
const DEFAULT_MAX_TOTAL_SIZE = 50 * 1024 * 1024

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <Image className="h-4 w-4 text-muted-foreground" />
  if (contentType === 'application/pdf') return <FileText className="h-4 w-4 text-muted-foreground" />
  return <FileIcon className="h-4 w-4 text-muted-foreground" />
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileUploadField({
  field, projectId, setValue, register, error,
}: {
  field: TriggerInputField
  projectId: string
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  register: ReturnType<typeof useForm<FormValues>>['register']
  error: { message?: string } | undefined
}) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileMetadata[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadWorkflowRunFiles(projectId)
  const deleteMutation = useDeleteWorkflowRunFile(projectId)

  const maxFileSize = field.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE
  const maxTotalSize = field.maxTotalSizeBytes ?? DEFAULT_MAX_TOTAL_SIZE
  const acceptFilter = field.accept ?? undefined
  const acceptExtensions = acceptFilter?.split(',').map(e => e.trim().toLowerCase()) ?? []

  const updateFormValue = useCallback((files: UploadedFileMetadata[]) => {
    setValue(field.name, files.length > 0 ? JSON.stringify(files) : '', { shouldValidate: true })
  }, [field.name, setValue])

  const validateFiles = useCallback((files: File[]): string | null => {
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      if (acceptExtensions.length > 0 && !acceptExtensions.includes(ext)) {
        return `File "${file.name}" has an unsupported type. Accepted: ${acceptExtensions.join(', ')}`
      }
      if (file.size > maxFileSize) {
        return `File "${file.name}" (${formatFileSize(file.size)}) exceeds the maximum of ${formatFileSize(maxFileSize)}`
      }
    }
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.sizeBytes, 0)
    const newTotal = currentTotal + files.reduce((sum, f) => sum + f.size, 0)
    if (newTotal > maxTotalSize) {
      return `Total upload size (${formatFileSize(newTotal)}) exceeds the maximum of ${formatFileSize(maxTotalSize)}`
    }
    return null
  }, [acceptExtensions, maxFileSize, maxTotalSize, uploadedFiles])

  const handleFiles = useCallback(async (files: File[]) => {
    setUploadError(null)
    const validationError = validateFiles(files)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    try {
      const response = await uploadMutation.mutateAsync(files)
      const newFiles = [...uploadedFiles, ...response.files]
      setUploadedFiles(newFiles)
      updateFormValue(newFiles)
    } catch (err: unknown) {
      setUploadError(extractValidationMessage(err) ?? 'Failed to upload files. Please try again.')
    }
  }, [validateFiles, uploadMutation, uploadedFiles, updateFormValue])

  const handleRemove = useCallback(async (index: number) => {
    if (confirmRemoveIndex === index) {
      const fileToRemove = uploadedFiles[index]
      const newFiles = uploadedFiles.filter((_, i) => i !== index)
      setUploadedFiles(newFiles)
      updateFormValue(newFiles)
      setUploadError(null)
      setConfirmRemoveIndex(null)

      try {
        await deleteMutation.mutateAsync(fileToRemove.blobPath)
      } catch {
        // Blob cleanup is best-effort; the file is already removed from the UI.
      }
    } else {
      setConfirmRemoveIndex(index)
    }
  }, [confirmRemoveIndex, uploadedFiles, updateFormValue, deleteMutation])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleFiles(files)
  }, [handleFiles])

  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {field.label ?? formatLabel(field.name)}
        {field.required && <span className="text-red-500"> *</span>}
      </Label>

      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer',
          isDragging ? 'border-button bg-brand-blue/5' : 'border-[rgba(145,149,167,0.5)] hover:border-[rgba(145,149,167,1)]',
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploadMutation.isPending ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Uploading...</span>
          </div>
        ) : (
          <>
            <Upload className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-foreground">
              Drag & drop files here, or <span className="text-button font-medium">browse</span>
            </p>
            {acceptFilter && (
              <p className="text-xs text-muted-foreground mt-1">
                Accepted: {acceptExtensions.map(e => e.replace('.', '').toUpperCase()).join(', ')}
              </p>
            )}
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptFilter}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            if (files.length > 0) handleFiles(files)
            e.target.value = ''
          }}
        />
      </div>

      {uploadedFiles.length > 0 && (
        <div className="space-y-1 mt-2">
          {uploadedFiles.map((file, index) => (
            <div key={file.blobPath} className="flex items-center gap-2 rounded-md border border-[rgba(145,149,167,0.3)] px-3 py-2">
              {getFileIcon(file.contentType)}
              <span className="text-sm text-foreground flex-1 truncate">{file.fileName}</span>
              <span className="text-xs text-muted-foreground">{formatFileSize(file.sizeBytes)}</span>
              {confirmRemoveIndex === index ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRemove(index) }}
                    className="text-xs font-medium text-destructive hover:underline"
                  >
                    Remove
                  </button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setConfirmRemoveIndex(null) }}
                    className="text-xs font-medium text-muted-foreground hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemove(index) }}
                  className="text-muted-foreground transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <input type="hidden" {...register(field.name, field.required ? { required: `${field.label ?? formatLabel(field.name)} is required` } : undefined)} />
      {(uploadError || error) ? (
        <p className="text-xs text-destructive" role="alert">{uploadError ?? error?.message}</p>
      ) : field.description ? (
        <p className={FIELD_HINT_CLASS}>{field.description}</p>
      ) : null}
    </div>
  )
}

export function DynamicField({
  field,
  projectId,
  register,
  errors,
  watch,
  setValue,
}: {
  field: TriggerInputField
  projectId: string
  register: ReturnType<typeof useForm<FormValues>>['register']
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
}) {
  if (!isFieldVisible(field, watch() as Record<string, unknown>)) return null

  const error = errors[field.name]

  if (field.hidden) {
    return <input type="hidden" {...register(field.name)} />
  }

  if (field.type === 'boolean') {
    const checked = watch(field.name) === 'true'
    return (
      <div className="space-y-2">
        <Label className={FIELD_LABEL_CLASS}>
          {field.label ?? formatLabel(field.name)}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>
        {field.description && <p className={FIELD_HINT_CLASS}>{field.description}</p>}
        <div className="flex items-center gap-4 h-12 rounded-lg px-2 py-2.5">
          <Checkbox
            id={`trigger-${field.name}`}
            data-testid={`trigger-${field.name}-checkbox`}
            className="h-5 w-5 rounded border-[rgba(217,217,217,1)]"
            checked={checked}
            onCheckedChange={(c) => setValue(field.name, c === true ? 'true' : 'false')}
          />
          <Label htmlFor={`trigger-${field.name}`} className="cursor-pointer text-sm font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Yes
          </Label>
        </div>
      </div>
    )
  }

  if (field.type === 'context-picker') {
    return <ContextPickerField field={field} projectId={projectId} watch={watch} setValue={setValue} register={register} error={error} />
  }

  if (field.type === 'connector-picker') {
    return <ConnectorPickerField field={field} projectId={projectId} watch={watch} setValue={setValue} register={register} error={error} />
  }

  if (field.type === 'testsuite-picker') {
    return <TestSuitePickerField field={field} projectId={projectId} watch={watch} setValue={setValue} register={register} error={error} />
  }

  if (field.type === 'repo-branch-map') {
    const dependencyFieldName = field.dependsOn ?? ''
    const rawSelected = dependencyFieldName ? (watch(dependencyFieldName) ?? '') : ''
    const selectedIds = (() => {
      if (typeof rawSelected === 'string' && rawSelected.startsWith('[')) {
        try {
          return JSON.parse(rawSelected) as string[]
        } catch {
          return []
        }
      }
      return rawSelected ? [rawSelected as string] : []
    })()
    const current = (watch(field.name) as string) ?? ''
    return (
      <>
        <input type="hidden" {...register(field.name)} />
        <RepoBranchMapField
          projectId={projectId}
          field={field}
          value={current}
          selectedContextSourceIds={selectedIds}
          onChange={(v) => setValue(field.name, v, { shouldValidate: true })}
        />
      </>
    )
  }

  if (field.type === 'repo-file-picker') {
    const current = (watch(field.name) as string) ?? ''
    return (
      <>
        <input type="hidden" {...register(field.name, {
          ...(field.required ? { required: `${field.label ?? formatLabel(field.name)} is required` } : {}),
          validate: (v) => validateRepoFilePickerValue((v as string) ?? '', field),
        })} />
        <RepoFilePickerField
          projectId={projectId}
          field={field}
          value={current}
          onChange={(v) => setValue(field.name, v, { shouldValidate: true })}
          error={error?.message as string | undefined}
        />
      </>
    )
  }

  if (field.type === 'file-upload') {
    return <FileUploadField field={field} projectId={projectId} setValue={setValue} register={register} error={error} />
  }

  if (field.options && field.options.length > 0) {
    const selectedValue = watch(field.name) ?? ''
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`trigger-${field.name}`} className={FIELD_LABEL_CLASS}>
          {field.label ?? formatLabel(field.name)}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>
        <Select
          value={selectedValue}
          onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}
        >
          <SelectTrigger id={`trigger-${field.name}`} className={FIELD_INPUT_CLASS} data-testid={`trigger-${field.name}-select`}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" {...register(field.name, field.required ? { required: `${field.label ?? formatLabel(field.name)} is required` } : undefined)} />
        {error ? (
          <p className="text-xs text-destructive" role="alert">{error.message as string}</p>
        ) : field.description ? (
          <p className={FIELD_HINT_CLASS}>{field.description}</p>
        ) : null}
      </div>
    )
  }

  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`trigger-${field.name}`} className={FIELD_LABEL_CLASS}>
          {field.label ?? formatLabel(field.name)}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>
        <Textarea
          id={`trigger-${field.name}`}
          data-testid={`trigger-${field.name}-textarea`}
          className={FIELD_INPUT_CLASS}
          rows={4}
          placeholder={field.default ? `Default: ${field.default}` : undefined}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={describedBy(field, error)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.stopPropagation() }}
          {...register(field.name, buildTextFieldRules(field, field.label ?? formatLabel(field.name)))}
        />
        {error ? (
          <p id={`trigger-${field.name}-error`} className="text-xs text-destructive" role="alert">{error.message as string}</p>
        ) : field.description ? (
          <p id={`trigger-${field.name}-hint`} className={FIELD_HINT_CLASS}>{field.description}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`trigger-${field.name}`} className={FIELD_LABEL_CLASS}>
        {field.label ?? formatLabel(field.name)}
        {field.required && <span className="text-red-500"> *</span>}
      </Label>
      <Input
        id={`trigger-${field.name}`}
        data-testid={`trigger-${field.name}-input`}
        type={field.type === 'secret' ? 'password' : 'text'}
        inputMode={field.type === 'number' ? 'numeric' : undefined}
        autoComplete={field.type === 'secret' ? 'new-password' : undefined}
        className={FIELD_INPUT_CLASS}
        placeholder={field.default ? `Default: ${field.default}` : undefined}
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy(field, error)}
        {...register(field.name, buildTextFieldRules(field, field.label ?? formatLabel(field.name)))}
      />
      {error ? (
        <p id={`trigger-${field.name}-error`} className="text-xs text-destructive" role="alert">{error.message as string}</p>
      ) : field.description ? (
        <p id={`trigger-${field.name}-hint`} className={FIELD_HINT_CLASS}>{field.description}</p>
      ) : null}
      {field.baseBranchMapField && field.baseBranchRepoField && (
        <BaseBranchHint
          projectId={projectId}
          mapValue={(watch(field.baseBranchMapField) as string) ?? ''}
          repoId={(watch(field.baseBranchRepoField) as string) ?? ''}
        />
      )}
      {field.branchNameInfo && <BranchNameHint />}
    </div>
  )
}

